import { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/apiClient";
import { todayLocal } from "@/lib/bookingDates";
import { formatCurrency } from "@/lib/money";
import type { MonthlyReport } from "@/types/camping";
import { DeltaBadge, LogicNote, PointDeltaBadge, StatCard, downloadCsv, formatPercent } from "@/pages/reports/reportShared";

function currentMonthValue(): string {
  const today = todayLocal();
  return today.slice(0, 7);
}

function formatMonthLabel(year: number, month: number): string {
  const d = new Date(year, month - 1, 1);
  return d.toLocaleDateString("de-DE", { month: "long", year: "numeric" });
}

export function MonthlyReportSection() {
  const [selectedMonth, setSelectedMonth] = useState(currentMonthValue);
  const [report, setReport] = useState<MonthlyReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [year, month] = useMemo(() => {
    const [y, m] = selectedMonth.split("-").map((p) => Number.parseInt(p, 10));
    return [y, m] as const;
  }, [selectedMonth]);

  const load = useCallback(() => {
    if (!year || !month) return;
    setLoading(true);
    setError(null);
    api
      .get<MonthlyReport>(`/api/v1/reports/monthly?year=${year}&month=${month}`)
      .then(setReport)
      .catch((err: unknown) => {
        setReport(null);
        setError(err instanceof Error ? err.message : "Monatsauswertung konnte nicht geladen werden.");
      })
      .finally(() => setLoading(false));
  }, [year, month]);

  useEffect(() => {
    load();
  }, [load]);

  const currency = report?.currency ?? "EUR";
  const monthLabel = report
    ? formatMonthLabel(report.year, report.month)
    : year && month
      ? formatMonthLabel(year, month)
      : selectedMonth;

  const exportCsv = () => {
    if (!report) return;
    const period = `${report.year}-${String(report.month).padStart(2, "0")}`;
    const rows: string[][] = [
      ["Kennzahl", "Wert"],
      ["Monat", period],
      ["Umsatz gesamt", report.invoiced_total],
      ["Gästenächte", String(report.guest_nights)],
      ["Belegte Platznächte", String(report.occupied_pitch_nights)],
      ["Verfügbare Platznächte", String(report.available_pitch_nights)],
      ["Auslastung %", report.occupancy_rate_percent ?? ""],
      ["Aktive Stellplätze", String(report.active_pitches)],
      ["Anreisen", String(report.arrivals_count)],
      ["Angereiste Personen", String(report.arriving_guests_count)],
    ];
    if (report.prior_month) {
      rows.push(
        [],
        ["Vormonat", `${report.prior_month.year}-${String(report.prior_month.month).padStart(2, "0")}`],
        ["Vormonat Umsatz", report.prior_month.invoiced_total],
        ["Vormonat Anreisen", String(report.prior_month.arrivals_count)],
        ["Vormonat angereiste Personen", String(report.prior_month.arriving_guests_count)],
        ["Vormonat Auslastung %", report.prior_month.occupancy_rate_percent ?? ""],
      );
    }
    downloadCsv(`monatsauswertung_${period}.csv`, rows);
  };

  return (
    <section className="space-y-6">
      <header className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h2 className="text-xl font-semibold text-label">Monatsauswertung</h2>
          <p className="mt-1 text-sm text-label-secondary">
            Umsatz und Belegung für {monthLabel}
          </p>
        </div>
        <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
          <label className="block text-sm">
            <span className="mb-1 block text-label-secondary">Monat</span>
            <input
              type="month"
              className="eh-input min-h-[44px] w-full sm:w-auto"
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(e.target.value)}
            />
          </label>
          <button
            type="button"
            className="eh-btn eh-btn--secondary min-h-[44px] whitespace-nowrap"
            disabled={!report}
            onClick={exportCsv}
          >
            CSV exportieren
          </button>
        </div>
      </header>

      {error ? (
        <p className="rounded-lg border border-status-red/30 bg-status-red/10 px-4 py-3 text-sm text-status-red">
          {error}
        </p>
      ) : null}

      {loading ? (
        <p className="text-sm text-label-secondary">Monatsauswertung wird geladen…</p>
      ) : report ? (
        <>
          <section className="rounded-xl border border-separator bg-surface p-4 shadow-sm">
            <div className="flex flex-wrap items-end justify-between gap-4">
              <div>
                <h3 className="font-medium text-label">Auslastung</h3>
                <p className="mt-1 text-sm text-label-secondary">
                  {report.occupied_pitch_nights.toLocaleString("de-DE")} von{" "}
                  {report.available_pitch_nights.toLocaleString("de-DE")} Platznächten ·{" "}
                  {report.active_pitches.toLocaleString("de-DE")} Stellplätze
                </p>
              </div>
              <div className="text-right">
                <div className="flex items-center justify-end gap-2">
                  <PointDeltaBadge
                    current={
                      report.occupancy_rate_percent != null
                        ? Number.parseFloat(report.occupancy_rate_percent)
                        : null
                    }
                    prior={
                      report.prior_month?.occupancy_rate_percent
                        ? Number.parseFloat(report.prior_month.occupancy_rate_percent)
                        : null
                    }
                  />
                  <p className="text-3xl font-semibold tabular-nums text-label">
                    {formatPercent(report.occupancy_rate_percent)}
                  </p>
                </div>
                {report.prior_month ? (
                  <p className="mt-1 text-xs text-label-secondary">
                    Vormonat {formatPercent(report.prior_month.occupancy_rate_percent)}
                  </p>
                ) : null}
              </div>
            </div>
            <div className="mt-4 h-3 overflow-hidden rounded-full bg-fill-tertiary">
              <div
                className="h-full rounded-full bg-accent"
                style={{
                  width: `${Math.min(
                    100,
                    Math.max(0, Number.parseFloat(report.occupancy_rate_percent ?? "0") || 0),
                  )}%`,
                }}
              />
            </div>
          </section>

          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            <StatCard
              label="Umsatz"
              value={formatCurrency(report.invoiced_total, currency)}
              delta={
                report.prior_month ? (
                  <DeltaBadge
                    current={Number.parseFloat(report.invoiced_total) || 0}
                    prior={Number.parseFloat(report.prior_month.invoiced_total) || 0}
                  />
                ) : undefined
              }
              hint={
                report.prior_month
                  ? `Vormonat ${formatCurrency(report.prior_month.invoiced_total, currency)}`
                  : "Nach Rechnungsdatum im Monat."
              }
            />
            <StatCard
              label="Anreisen"
              value={String(report.arrivals_count)}
              delta={
                report.prior_month ? (
                  <DeltaBadge
                    current={report.arrivals_count}
                    prior={report.prior_month.arrivals_count}
                  />
                ) : undefined
              }
              hint={
                report.prior_month
                  ? `Vormonat ${report.prior_month.arrivals_count.toLocaleString("de-DE")}`
                  : "Buchungen mit Anreise im Monat."
              }
            />
            <StatCard
              label="Gäste"
              value={String(report.arriving_guests_count)}
              delta={
                report.prior_month ? (
                  <DeltaBadge
                    current={report.arriving_guests_count}
                    prior={report.prior_month.arriving_guests_count}
                  />
                ) : undefined
              }
              hint={
                report.prior_month
                  ? `Vormonat ${report.prior_month.arriving_guests_count.toLocaleString("de-DE")} Personen`
                  : "Erwachsene und Kinder aller Anreisen im Monat."
              }
            />
            <StatCard label="Gästenächte" value={String(report.guest_nights)} />
            <StatCard
              label="Belegte Platznächte"
              value={String(report.occupied_pitch_nights)}
              hint={`${report.active_pitches} aktive Stellplätze`}
            />
          </div>

          <LogicNote>
            <p>{report.logic_note}</p>
            <p>
              Der Vergleich in dieser Ansicht gilt zum Vormonat. Der tagesaktuelle Vergleich zum Vorjahr
              steht im Überblick.
            </p>
          </LogicNote>
        </>
      ) : null}
    </section>
  );
}
